import { Box, Flex, Modal, ModalContent, ModalTitle, Text } from '@modusign/design-system';
import { useRef, useState } from 'react';
import { useUnmount, useUpdateEffect } from 'react-use';

import noop from '@libs/noop';

import ProgressBar from '@app/components/ProgressBar';

import useIntervalIncreaseStep from '../../hooks/useIntervalIncreaseStep';

type LoadingStatus = 'idle' | 'loading' | 'success' | 'error';

interface ProgressModalProps {
  loadingStatus: LoadingStatus;
}

export default function ProgressModal({ loadingStatus }: ProgressModalProps) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { step, resetStep, setToMaxStep, startIntervalIncrease } = useIntervalIncreaseStep({
    initStep: 1,
    maxStep: 10,
    interval: 800,
    stepSize: 0.5,
  });

  useUnmount(() => timerRef.current && clearTimeout(timerRef.current));

  useUpdateEffect(() => {
    const handleLoadingStatus = (loadingStatus: LoadingStatus) => {
      switch (loadingStatus) {
        case 'idle':
          resetStep();
          break;
        case 'loading':
          startIntervalIncrease();
          setShowModal(true);
          break;
        case 'success':
          setToMaxStep();
          timerRef.current = setTimeout(() => setShowModal(false), 500);
          break;
        case 'error':
        default:
          setToMaxStep();
          setShowModal(false);
          break;
      }
    };

    handleLoadingStatus(loadingStatus);
  }, [loadingStatus]);

  return <ProgressModalContent showModal={showModal} step={step} />;
}

function ProgressModalContent({ showModal, step }: { showModal: boolean; step: number }) {
  return (
    <Modal isOpen={showModal} maxWidth="sm" onClose={noop}>
      <ModalTitle>문서 검증 중</ModalTitle>
      <ModalContent>
        <Flex alignItems="center" flexDirection="column">
          <Text fontSize="md" fontWeight="regular" mb={6} textAlign="center">
            문서 검증을 완료할 때까지 브라우저를 닫지 마세요.
            <br />
            문서에 따라 최대 5분 정도 소요될 수 있습니다.
          </Text>
          <Box px={10} width="100%">
            <ProgressBar currentProgressPercent={step * 10} width="100%" />
          </Box>
        </Flex>
      </ModalContent>
    </Modal>
  );
}
